import { AlertGroup, AlertRule, AlertSensor, AlertTemplate, AlertVariables } from '../types/Alerts'
import { AlertRuleDataGridRow } from '../components/AlertRuleDataGrid'
import { UserAlertRuleDataGridRow } from '../components/UserAlertRuleDialog'
import { PrometheusAlert, Sensor, UserAlertRule, UserAlertRuleInput } from '../types'
import { AlertRuleName, AlertPhase, AlertSeverity, AlertStatus, AlertRuleGroupName } from '../enums'
import i18next from 'i18next'
import { AlertRuleField } from '../components/AlertRuleFieldSelect'
import { AlertRuleFunction } from '../components/AlertRuleFunctionSelect'
import { Alert } from '@mui/material'

export function buildAlertStructure(
  data: AlertRuleDataGridRow[],
  alertTemplates: AlertTemplate[]
): AlertGroup[] {
  const alertRules: AlertGroup[] = alertTemplates.map((template): AlertGroup => {
    return {
      name: template.name,
      alerts: Object.entries(template.defaults).map(([alertName, alert]): AlertRule => {
        return {
          name: alertName as AlertRuleName,
          enabled: alert.enabled,
          variables: alert.variables
        }
      })
    }
  })

  data.forEach((row: AlertRuleDataGridRow) => {
    const alertGroup = alertRules.find((template) => template.name === row.group) as AlertGroup

    const alertRule: AlertRule = {
      name: row.name,
      enabled: row.enabled,
      sensor: row?.sensor,
      severity: row?.severity,
      variables: row.variables
    }

    const alertIndex = alertGroup.alerts.findIndex((alert) => alert.name === row.name)
    if (alertIndex === -1) {
      alertGroup.alerts.push(alertRule)
    } else {
      alertGroup.alerts[alertIndex] = alertRule
    }
  })

  return alertRules
}

export function checkAlertValueUnit(
  alertname: string,
  projectAlertRules: AlertGroup[],
  variable?: keyof AlertVariables
) {
  let unit
  projectAlertRules.map((group: AlertGroup) => {
    group.alerts.map((alert: AlertRule) => {
      if (alert.name === alertname) {
        if (variable) {
          unit = alert.variables[variable]?.unit
        } else {
          unit = alert.variables.param1.unit
        }
      }
    })
  })
  return unit
}

export function buildAlertStructureForUser(data: UserAlertRuleDataGridRow[]) {
  const alertRules: UserAlertRuleInput[] = []

  data.map((rule: UserAlertRuleDataGridRow) => {
    if (rule?.enabled || rule.appNotificationEnabled || rule.emailNotificationEnabled)
      alertRules.push({
        ruleGroupName: rule.ruleGroupName,
        alertRuleName: rule.alertRuleName,
        emailNotificationEnabled: rule?.enabled ?? rule.emailNotificationEnabled,
        smsNotificationEnabled: rule?.enabled ?? rule.smsNotificationEnabled,
        appNotificationEnabled: rule?.enabled ?? rule.appNotificationEnabled
      })
  })

  return alertRules
}

export function isDeviceAlerting(
  sensor: Sensor | null,
  alertRules: AlertGroup[]
): AlertRuleName | null {
  if (!sensor || !alertRules?.length) {
    return null
  }

  for (const group of alertRules) {
    for (const alert of group.alerts) {
      if (alert.enabled && compareValues(sensor, alert)) {
        return alert.name as AlertRuleName
      }
    }
  }

  return null
}

function compareValues(sensor: Sensor, alert: AlertRule): boolean {
  if (!('param1' in alert.variables) || typeof alert.variables.param1.value !== 'number') {
    return false
  }

  switch (alert.name) {
    case AlertRuleName.SHORT_CIRCUIT:
      return (
        (sensor.current_1 / sensor.rated_current) * 100 > alert.variables.param1.value ||
        (sensor.current_2 / sensor.rated_current) * 100 > alert.variables.param1.value ||
        (sensor.current_3 / sensor.rated_current) * 100 > alert.variables.param1.value
      )
    case AlertRuleName.HIGH_CURRENT:
      return (
        (sensor.current_1 / sensor.rated_current) * 100 > alert.variables.param1.value ||
        (sensor.current_2 / sensor.rated_current) * 100 > alert.variables.param1.value ||
        (sensor.current_3 / sensor.rated_current) * 100 > alert.variables.param1.value
      )
    case AlertRuleName.OVERLOADED_CURRENT:
      return (
        (sensor.current_1 / sensor.rated_current) * 100 > alert.variables.param1.value ||
        (sensor.current_2 / sensor.rated_current) * 100 > alert.variables.param1.value ||
        (sensor.current_3 / sensor.rated_current) * 100 > alert.variables.param1.value
      )
    case AlertRuleName.HIGH_HUMIDITY:
      return sensor.rh > alert.variables.param1.value
    case AlertRuleName.LOW_SIGNAL:
      return sensor.rssi < alert.variables.param1.value
    case AlertRuleName.HIGH_TEMPERATURE:
      return sensor.temp > alert.variables.param1.value
    case AlertRuleName.HIGH_VOLTAGE:
      return (
        sensor.voltage_1 > alert.variables.param1.value ||
        sensor.voltage_2 > alert.variables.param1.value ||
        sensor.voltage_3 > alert.variables.param1.value
      )
    case AlertRuleName.LOW_VOLTAGE:
      return (
        sensor.voltage_1 < alert.variables.param1.value ||
        sensor.voltage_2 < alert.variables.param1.value ||
        sensor.voltage_3 < alert.variables.param1.value
      )
    default:
      return false
  }
}

/**
 * Translates the alert rule name.
 */
export function translateAlertRuleName(name: AlertRuleName) {
  return i18next.t(`AlertRuleName.${name}`, { ns: 'alert' })
}

/**
 * Translates the alert rule group name.
 */
export function translateAlertRuleGroupName(group: AlertRuleGroupName) {
  return i18next.t(`AlertRuleGroupName.${group}`, { ns: 'alert' })
}

/**
 * Translate an alert severity.
 */
export function translateAlertSeverity(severity: AlertSeverity) {
  return i18next.t(`AlertSeverity.${severity}`, { ns: 'alert' })
}

/**
 * Translate the alert status.
 */
export function translateAlertStatus(status: AlertStatus) {
  return i18next.t(`AlertStatus.${status}`, { ns: 'alert' })
}

export function translateAlertPhase(phase: AlertPhase) {
  return i18next.t(`AlertPhase.${phase}`, { ns: 'alert' })
}

/**
 * Get the alert name.
 */
export function getAlertRuleName(alert: PrometheusAlert) {
  return alert?.labels?.alertname || AlertRuleName.UNKNOWN
}

/**
 * Get the alert severity.
 */
export function getAlertSeverity(alert: PrometheusAlert) {
  return alert?.labels?.severity || AlertSeverity.UNKNOWN
}

/**
 * Get the alert status.
 */
export function getAlertStatus(alert: PrometheusAlert) {
  return alert?.state || alert?.status || AlertStatus.UNKNOWN
}

/**
 * Get the unit of the alert value.
 */
export function getAlertValueUnit(alert: PrometheusAlert) {
  switch (alert.labels.alertname) {
    case AlertRuleName.HIGH_CURRENT:
    case AlertRuleName.OVERLOADED_CURRENT:
    case AlertRuleName.SHORT_CIRCUIT:
    case AlertRuleName.HIGH_HUMIDITY:
      return '%'
    case AlertRuleName.LOW_SIGNAL:
      return 'dBm'
    case AlertRuleName.HIGH_TEMPERATURE:
      return '°C'
    case AlertRuleName.HIGH_VOLTAGE:
    case AlertRuleName.LOW_VOLTAGE:
      return 'V'
  }
}

export function getAlertValue(alert: PrometheusAlert) {
  return alert?.value !== undefined
    ? alert.value
    : alert.annotations?.value
      ? parseFloat(alert.annotations.value)
      : null
}

export function getHumanizedAlertValue(alert: PrometheusAlert) {
  const unit = getAlertValueUnit(alert)
  const value = getAlertValue(alert)

  if (value === null) {
    return '-'
  } else if (unit !== undefined) {
    return `${value.toFixed(1)} ${unit}`
  } else {
    return `${value.toFixed(1)}`
  }
}

export function getAlertThresholdValue(alert: PrometheusAlert) {
  const param1 = alert?.labels?.param1
  return param1 ? parseFloat(param1) : null
}

export function getHumanizedAlertThresholdValue(alert: PrometheusAlert) {
  const unit = getAlertValueUnit(alert)
  const thresholdValue = getAlertThresholdValue(alert)

  if (thresholdValue === null) {
    return '-'
  } else if (unit === '%') {
    return `${(thresholdValue * 100).toFixed(0)} ${unit}`
  } else if (unit !== undefined) {
    return `${thresholdValue.toFixed(1)} ${unit}`
  } else {
    return `${thresholdValue.toFixed(1)}`
  }
}

export function getAlertTimestamp(alert: PrometheusAlert) {
  if (alert?.startsAt) {
    return new Date(alert.startsAt)
  } else if (alert?.activeAt) {
    return new Date(alert.activeAt)
  } else {
    return null
  }
}

export function sortAlertsByDate(alerts: PrometheusAlert[]) {
  return alerts.sort((a, b) => {
    const aDate = getAlertTimestamp(a)
    const bDate = getAlertTimestamp(b)

    if (aDate && bDate) {
      return bDate.getTime() - aDate.getTime()
    } else if (aDate) {
      return -1
    } else if (bDate) {
      return 1
    } else {
      return 0
    }
  })
}

export function getUniqueAlerts(alerts: PrometheusAlert[]) {
  alerts = sortAlertsByDate(alerts)

  return alerts.reduce((uniqueAlerts: PrometheusAlert[], alert) => {
    const existingAlert = uniqueAlerts.find(
      (a) =>
        a.labels.device === alert.labels.device &&
        a.labels.alertname === alert.labels.alertname &&
        a.labels.name === alert.labels.name
    )

    if (!existingAlert) {
      uniqueAlerts.push(alert)
    }

    return uniqueAlerts
  }, [])
}

export function makeAlertRuleDataGridRow(
  group: AlertGroup,
  rule: AlertRule,
  userAlertRule?: UserAlertRule
): AlertRuleDataGridRow {
  return {
    id: rule.name,
    group: group.name,
    name: rule.name,
    sensor: rule?.sensor,
    severity: rule?.severity,
    enabled: rule.enabled,
    variables: rule.variables,
    userAlertRule
  }
}

export function makeAlertRuleDataGridRows(
  alertRules: AlertGroup[],
  userAlertRules: UserAlertRule[],
  enabled = true
) {
  return alertRules
    .map((group) =>
      group.alerts
        .filter((rule) => rule.enabled === enabled)
        .map((rule) => {
          const userAlertRule = userAlertRules.find(
            (userAlertRule) => userAlertRule.alertRuleName === rule.name
          )
          return makeAlertRuleDataGridRow(group, rule, userAlertRule)
        })
    )
    .flat()
}

export function translateAlertRuleVariable(
  rule: AlertRule | AlertRuleDataGridRow,
  key: keyof AlertVariables
): string {
  const field = getFieldByAlertRuleVariable(rule as AlertRuleDataGridRow, key)
  return translateAlertRuleField(field)
}

export function translateAlertRuleField(field: AlertRuleField): string {
  return i18next.t(`AlertRuleField.${field}`, { ns: 'alert' })
}

export function getFieldByAlertRuleVariable(
  row: AlertRule | AlertRuleDataGridRow,
  key: keyof AlertVariables
): AlertRuleField {
  switch (row.name) {
    case AlertRuleName.HIGH_CURRENT:
    case AlertRuleName.OVERLOADED_CURRENT:
    case AlertRuleName.SHORT_CIRCUIT:
      return 'current'
    case AlertRuleName.HIGH_VOLTAGE:
    case AlertRuleName.LOW_VOLTAGE:
    case AlertRuleName.NO_VOLTAGE:
    case AlertRuleName.LOW_BATTERY:
      return 'voltage'
    case AlertRuleName.HIGH_TEMPERATURE:
    case AlertRuleName.LOW_TEMPERATURE:
      return 'temperature'
    case AlertRuleName.HIGH_HUMIDITY:
    case AlertRuleName.LOW_HUMIDITY:
      return 'humidity'
    case AlertRuleName.DEW_POINT:
      return 'dewPoint'
    case AlertRuleName.HIGH_PRESSURE:
    case AlertRuleName.LOW_PRESSURE:
      return 'pressure'
    case AlertRuleName.CONDENSTATION_RISK_INTAKE:
      return 'inDewPoint'
    case AlertRuleName.CONDENSATION_RISK_EXHAUST:
      return 'outDewPoint'
    case AlertRuleName.DEVICE_OFFLINE:
      return 'minutes'
    case AlertRuleName.EXHAUST_AIR_TOO_COLD:
    case AlertRuleName.EXHAUST_AIR_TOO_HOT:
      return 'temperature'
    case AlertRuleName.HEATING_WHILE_VALVE_CLOSED:
    case AlertRuleName.NO_HEATING:
      switch (key) {
        case 'param1':
          return 'temperature'
        case 'param2':
          return 'setPoint'
        default:
          return 'unknown'
      }
    case AlertRuleName.TARGET_TEMPERATURE_NOT_REACHED:
      switch (key) {
        case 'param1':
          return 'temperature'
        case 'param2':
          return 'minutes'
        default:
          return 'unknown'
      }
    case AlertRuleName.RAPID_TEMPERATURE_DROP:
    case AlertRuleName.RAPID_TEMPERATURE_RISE:
      return 'temperature'
    case AlertRuleName.WEAK_SIGNAL:
    case AlertRuleName.LOW_SIGNAL:
      return 'rssi'
    default:
      return 'unknown'
  }
}

export function deepMergeAlertGroups(
  defaults: AlertGroup[],
  alertGroups: AlertGroup[],
  sensor?: AlertSensor
): AlertGroup[] {
  const deepMergedAlertGroups = [...defaults]

  for (const alertGroup of alertGroups) {
    const index = deepMergedAlertGroups.findIndex((g) => g.name === alertGroup.name)
    if (index === -1) {
      deepMergedAlertGroups.push(alertGroup)
    } else {
      deepMergedAlertGroups[index] = {
        name: alertGroup.name,
        alerts: deepMergeAlertRules(deepMergedAlertGroups[index].alerts, alertGroup.alerts, sensor)
      }
    }
  }

  return deepMergedAlertGroups
}

export function deepMergeAlertRules(
  defaults: AlertRule[],
  alertRules: AlertRule[],
  sensor?: AlertSensor
): AlertRule[] {
  const deepMergedAlertRules = defaults.map((rule) => ({ ...rule, sensor }) as AlertRule)

  for (const alertRule of alertRules) {
    const index = deepMergedAlertRules.findIndex((r) => r.name === alertRule.name)
    if (index === -1) {
      deepMergedAlertRules.push(alertRule)
    } else {
      if (!deepMergedAlertRules[index].enabled) {
        deepMergedAlertRules[index] = { ...alertRule }
      } else {
        deepMergedAlertRules[index] = { ...alertRule, enabled: true, sensor }
      }
    }
  }

  return deepMergedAlertRules
}

export function isProjectAlertTemplate(template: AlertTemplate): boolean {
  return (
    Object.values(template.rules).some((rule) => rule.labels.sensor === undefined) &&
    isProjectAlertGroupName(template.name)
  )
}

export function isProjectAlertGroup(group: AlertGroup): boolean {
  return (
    group.alerts.some((alert) => alert.sensor === undefined) && isProjectAlertGroupName(group.name)
  )
}

function isProjectAlertGroupName(name: AlertRuleGroupName): boolean {
  return (
    name === AlertRuleGroupName.CURRENT ||
    name === AlertRuleGroupName.DEVICE ||
    name === AlertRuleGroupName.HUMIDITY ||
    name === AlertRuleGroupName.SIGNAL ||
    name === AlertRuleGroupName.TEMPERATURE ||
    name === AlertRuleGroupName.VOLTAGE
  )
}

export function isDeviceAlertTemplate(template: AlertTemplate): boolean {
  return (
    Object.values(template.rules).some((rule) => rule.labels.sensor === AlertSensor.DEVICE) &&
    isDeviceAlertGroupName(template.name)
  )
}

export function isDeviceAlertGroup(group: AlertGroup): boolean {
  return (
    group.alerts.some((alert) => alert.sensor === AlertSensor.DEVICE) &&
    isDeviceAlertGroupName(group.name)
  )
}

function isDeviceAlertGroupName(name: AlertRuleGroupName): boolean {
  return (
    name === AlertRuleGroupName.CURRENT ||
    name === AlertRuleGroupName.DEVICE ||
    name === AlertRuleGroupName.HUMIDITY ||
    name === AlertRuleGroupName.SIGNAL ||
    name === AlertRuleGroupName.TEMPERATURE ||
    name === AlertRuleGroupName.VOLTAGE
  )
}

export function isValveAlertTemplate(template: AlertTemplate): boolean {
  return (
    Object.values(template.rules).some((rule) => rule.labels.sensor === AlertSensor.VALVE) &&
    isValveAlertGroupName(template.name)
  )
}

export function isValveAlertGroup(alertGroup: AlertGroup): boolean {
  return (
    alertGroup.alerts.some((alert) => alert.sensor === AlertSensor.VALVE) &&
    isValveAlertGroupName(alertGroup.name)
  )
}

function isValveAlertGroupName(name: AlertRuleGroupName): boolean {
  return (
    name === AlertRuleGroupName.CONDENSATION_RISK ||
    name === AlertRuleGroupName.DEVICE_OFFLINE ||
    name === AlertRuleGroupName.EXHAUST_AIR_TOO_COLD ||
    name === AlertRuleGroupName.EXHAUST_AIR_TOO_HOT ||
    name === AlertRuleGroupName.HEATING_WHILE_VALVE_CLOSED ||
    name === AlertRuleGroupName.LOW_BATTERY ||
    name === AlertRuleGroupName.NO_HEATING ||
    name === AlertRuleGroupName.TARGET_TEMPERATURE_NOT_REACHED ||
    name === AlertRuleGroupName.TEMPERATURE_RAPID_CHANGE ||
    name === AlertRuleGroupName.WEAK_SIGNAL
  )
}

export function isRuuviTagAlertTemplate(template: AlertTemplate): boolean {
  return (
    Object.values(template.rules).some((rule) => rule.labels.sensor === AlertSensor.RUUVITAG) &&
    isRuuviTagAlertGroupName(template.name)
  )
}

export function isRuuviTagAlertGroup(alertGroup: AlertGroup): boolean {
  return (
    alertGroup.alerts.some((alert) => alert.sensor === AlertSensor.RUUVITAG) &&
    isRuuviTagAlertGroupName(alertGroup.name)
  )
}

function isRuuviTagAlertGroupName(name: AlertRuleGroupName): boolean {
  return (
    name === AlertRuleGroupName.BATTERY ||
    name === AlertRuleGroupName.DEW_POINT ||
    name === AlertRuleGroupName.HUMIDITY ||
    name === AlertRuleGroupName.PRESSURE ||
    name === AlertRuleGroupName.TEMPERATURE
  )
}

export function isProjectAlertRule(rule: AlertRule | AlertRuleDataGridRow) {
  return rule.sensor === undefined
}

export function isDeviceAlertRule(rule: AlertRule | AlertRuleDataGridRow) {
  return rule.sensor === AlertSensor.DEVICE
}

export function isValveAlertRule(rule: AlertRule | AlertRuleDataGridRow) {
  return rule.sensor === AlertSensor.VALVE
}

export function isRuuviTagAlertRule(rule: AlertRule | AlertRuleDataGridRow) {
  return rule.sensor === AlertSensor.RUUVITAG
}
